import React from "react";
import { CheckCircle2, X } from "lucide-react";

/**
 * "Save this view" pop-up — shown top-right, under the workspace switcher,
 * either automatically a short time after arriving from an onboarding short
 * link/combined link, or from the Active filters row's "+ Save as Shortcut"
 * link. Copy/behavior matches the reference prototype (v3product.maybedemo.com):
 * clicking "Save shortcut" doesn't close the pop-up, it morphs it into a
 * "Saved ... / Show me" confirmation (`saved` prop) that stays until
 * dismissed; "Show me" also highlights the new row in the sidebar (see
 * lib/shortcuts.js's requestShortcutHighlight, wired from the caller via
 * onShowMe). No text input in either state — the name is auto-derived from
 * the active filters; renaming happens afterward via the "Saved as" pencil.
 */
const SaveShortcutPopup = ({ label, saved, onSave, onDismiss, onShowMe }) => {
  if (saved) {
    return (
      <div className="fixed top-16 right-4 z-50 w-80 rounded-xl border border-border bg-background p-4 shadow-lg">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" aria-hidden="true" />
            <p className="text-sm text-foreground">
              Saved "{label}" to Shortcuts in the sidebar.
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 ml-6">
          Find it under Shortcuts on the left.
        </p>
        <button
          type="button"
          onClick={onShowMe}
          className="mt-3 h-8 px-3 rounded-md bg-foreground text-background text-xs font-medium"
        >
          Show me
        </button>
      </div>
    );
  }

  return (
    <div className="fixed top-16 right-4 z-50 w-72 rounded-xl border border-border bg-background p-4 shadow-lg">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-foreground">Showing {label} only.</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">
        Save this as a shortcut to find it again from the sidebar.
      </p>
      <button
        type="button"
        onClick={onSave}
        className="mt-3 h-8 px-3 rounded-md bg-foreground text-background text-xs font-medium"
      >
        Save shortcut
      </button>
    </div>
  );
};

export default SaveShortcutPopup;
